import { toast } from '@tamagui/toast/v2';
import { format, parseISO } from 'date-fns';
import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useEffect, useRef, useState } from 'react';
import { XStack, YStack } from 'tamagui';

import CheckoutCart from '@/components/CheckoutCart';
import CheckoutEventHeader from '@/components/CheckoutEventHeader';
import CheckoutUserDetails from '@/components/CheckoutUserDetails';
import Section from '@/components/Section';
import { PSHeaderBackButtonOnTransparentHeader } from '@/components/PSHeaderButtons';
import SocialLinkSheet from '@/components/SocialLinkSheet';
import PSBottomBar from '@/components/ui/PSBottomBar';
import PSButton from '@/components/ui/PSButton';
import PSLinkButton from '@/components/ui/PSLinkButton';
import PSRichText from '@/components/ui/PSRichText';
import PSText from '@/components/ui/PSText';
import PageScrollView from '@/components/ui/PageScrollView';
import { asApiError } from '@/src/api/apiError';
import authApi, { UserProfile } from '@/src/api/authApi';
import bookingsApi from '@/src/api/bookingsApi';
import type { Event, EventSlot } from '@/src/api/eventsApi';
import { newIdempotencyKey } from '@/src/api/idempotency';
import participationRequestsApi from '@/src/api/participationRequestsApi';
import { useEventCart, type CartLine } from '@/src/lib/eventCart';
import { openPaymentSheet } from '@/src/lib/razorpayCheckout';
import { setRestoredUser, useSessionUser } from '@/src/lib/sessionStore';
import { openURL } from '@/src/utils/linkingUtils';
import { normalizeSocialLink } from '@/src/utils/validation';
import { RequestNotice } from '@/components/RequestNotice';
import EventLocation from '@/components/EventLocation';
import { formatDateTime } from '@/src/utils/dateFormatter';
import Footer from '@/components/Footer';
import PSCouponCodeInput from '@/components/PSCouponCodeInput';

/**
 * The confirm / payment step for an event — where "Book now" on `[id]/index.tsx`
 * lands. It shows the user what they're about to buy: the event identity, the
 * venue, the slot they picked, an itemised cart, their contact details, and the
 * terms — over a bar that pays for it.
 *
 * It DERIVES almost nothing. The event, the picked slot, the cart lines and the
 * total all come from `useEventCart` (mounted in this route's layout), already
 * added up and already formatted — this screen only lays them out. That's also
 * why arriving here is free: the detail screen's fetch is the one being read.
 *
 * The one thing it OWNS is the booking press — the state machine below.
 *
 * ── Loading / error ──
 * Both are placeholders. Coming from the detail screen the event is already
 * loaded, so neither branch is reachable today — they exist because resuming an
 * ABANDONED booking will land the user here directly, with the provider mounting
 * and fetching from cold. That's when they get designed (see `src/lib/eventCart`).
 */
export default function ConfirmPage() {
  const {
    eventState,
    selectedSlot,
    lines,
    bookingItems,
    subtotalDisplay,
    isSubtotalFree,
    totalDisplay,
    isFree,
    approvalTier,
    socialLink,
    setSocialLink,
    couponCode,
    couponQuote,
    applyCoupon,
    removeCoupon,
  } = useEventCart();
  const user = useSessionUser();
  const router = useRouter();
  const [isBooking, setIsBooking] = useState(false);

  const [socialSheetOpen, setSocialSheetOpen] = useState(false);
  const [socialLinkDraft, setSocialLinkDraft] = useState(socialLink ?? '');
  const [socialLinkError, setSocialLinkError] = useState<string | undefined>();
  const [isSavingSocialLink, setIsSavingSocialLink] = useState(false);
  const onEditSocialLink = () => {
    setSocialLinkDraft(socialLink ?? '');
    setSocialLinkError(undefined);
    setSocialSheetOpen(true);
  };
  // Same save as the detail screen's first ask: the link is the user's, not
  // this booking's answer, so an edit here is a `PATCH /me` too — otherwise the
  // profile would keep the link that was replaced, and the next event would
  // prefill it. The cart is only updated once the save lands, so the receipt
  // above can't show a link the server doesn't have.
  const onSubmitSocialLink = async () => {
    const normalized = normalizeSocialLink(socialLinkDraft);
    if (!normalized) {
      setSocialLinkError(
        'Enter a valid Instagram, X, or LinkedIn profile link.'
      );
      return;
    }
    setIsSavingSocialLink(true);
    try {
      const { user: updated } = await authApi.updateMe({
        socialMediaLink: normalized,
      });
      setRestoredUser(updated);
      setSocialLink(normalized);
      setSocialSheetOpen(false);
    } catch (caught) {
      const failure = asApiError(caught);
      if (failure.kind === 'api') setSocialLinkError(failure.message);
      else toast.error(failure.message);
    } finally {
      setIsSavingSocialLink(false);
    }
  };

  // Re-minted whenever the coupon changes, so a retry after a cancelled sheet
  // can't replay a booking priced differently.
  const idempotencyKey = useRef(newIdempotencyKey());

  const [couponDraft, setCouponDraft] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [couponError, setCouponError] = useState<string | undefined>();
  const priceCoupon = async (code: string): Promise<boolean> => {
    if (eventState.status !== 'success' || bookingItems.length === 0) {
      return false;
    }
    setIsRedeeming(true);
    setCouponError(undefined);
    try {
      const quote = await bookingsApi.previewBooking({
        eventId: eventState.event.id,
        items: bookingItems,
        couponCode: code,
      });
      applyCoupon(code, quote);
      setCouponDraft('');
      idempotencyKey.current = newIdempotencyKey();
      return true;
    } catch (caught) {
      const failure = asApiError(caught);
      if (failure.kind === 'api') setCouponError(failure.message);
      else toast.error(failure.message);
      return false;
    } finally {
      setIsRedeeming(false);
    }
  };

  // Re-price the cart's code against the cart as it is now — the user may have
  // changed tickets since redeeming it. A code that no longer applies is
  // dropped, so the field never reads "applied" beside an undiscounted total.
  // Keyed on `bookingItems` alone: they settle once the event loads, and a
  // redeem (which sets `couponCode`) has already priced itself.
  useEffect(() => {
    if (!couponCode || isSubtotalFree || bookingItems.length === 0) return;
    // Fetch-on-change: the sync setState is the request's loading flag.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    priceCoupon(couponCode).then((priced) => {
      if (!priced) removeCoupon();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingItems]);
  const onRemoveCoupon = () => {
    removeCoupon();
    idempotencyKey.current = newIdempotencyKey();
  };

  const handleBook = async () => {
    if (eventState.status !== 'success' || bookingItems.length === 0) return;

    setIsBooking(true);
    try {
      if (approvalTier) {
        const request =
          await participationRequestsApi.createParticipationRequest({
            eventId: eventState.event.id,
            slotTierId: approvalTier.slotTierId,
            socialLink: socialLink ?? undefined,
          });
        router.replace(`/requests/${request.id}`);
        return;
      }

      // The items come STRAIGHT from the cart — it holds the selection keyed by
      // `slotTierId`, so there's nothing to remap here (see [[eventCart]]).
      const booking = await bookingsApi.createBooking({
        eventId: eventState.event.id,
        items: bookingItems,
        couponCode: couponQuote?.couponCode ?? undefined,
        idempotencyKey: idempotencyKey.current,
      });

      if (booking.payment) {
        const outcome = await openPaymentSheet(booking.payment);
        if (outcome.status === 'cancelled') {
          // Not `toast.error`: nothing broke, and the red card is the app's
          // colour for a failure (see PSToastHost). This is the neutral card.
          setIsBooking(false);
          return;
        }
      }

      router.replace(`/bookings/${booking.id}`);
    } catch (error) {
      // One catch for both steps, because both throw the same class. A rejected
      // create describes itself in words meant for the user (sold out, not enough
      // seats, a closed slot); so does a declined payment (Razorpay's own text
      // about the card or the bank). `asApiError` guarantees a message even when
      // the throw came from our own mapping.
      const failure = asApiError(error);
      // The preview held nothing, so the coupon can still be refused here (cap
      // hit, expired since). Every such refusal names the field; un-apply the
      // code so a retry doesn't resend it.
      if (failure.field === 'coupon_code' && couponCode) {
        onRemoveCoupon();
        setCouponDraft(couponCode);
        setCouponError(failure.message);
      } else {
        toast.error(failure.message);
      }
      // Reset ONLY on failure. On success we're navigating away, and flipping
      // the button back to "Pay now" during the transition reads as if nothing
      // happened; leaving it in flight until the screen unmounts is the truth.
      setIsBooking(false);
    }
  };

  if (eventState.status === 'loading') {
    return <FullScreenMessage message="Loading…" />;
  }

  // TODO: this is bad, user can't do anything on this screen
  if (eventState.status === 'error') {
    return <FullScreenMessage message="Oops" />;
  }

  // No slot, an empty cart, or no user (a deep-link straight here) — the empty
  // state is unbuilt for now, so render nothing rather than a blank receipt. The
  // user IS guaranteed on the normal path (the detail screen's CTA routes an
  // incomplete profile into auth first), but the session type is nullable.
  if (selectedSlot === null || lines.length === 0 || user === null) {
    return null;
  }

  return (
    <>
      <ConfirmBody
        event={eventState.event}
        slot={selectedSlot}
        lines={lines}
        totalDisplay={totalDisplay}
        subtotalDisplay={couponQuote ? subtotalDisplay : undefined}
        couponDiscountDisplay={
          couponQuote ? `-${couponQuote.discount.display}` : undefined
        }
        isSubtotalFree={isSubtotalFree}
        isFree={isFree}
        user={user}
        onBook={handleBook}
        isBooking={isBooking}
        requiresApproval={approvalTier !== null}
        socialLink={socialLink ?? undefined}
        onEditSocialLink={onEditSocialLink}
        appliedCouponCode={couponCode ?? undefined}
        isRedeemingCoupon={isRedeeming}
        couponError={couponError}
        onRedeemCoupon={priceCoupon}
        onRemoveCoupon={onRemoveCoupon}
        couponDraft={couponDraft}
        onChangeCouponDraft={(code) => {
          setCouponDraft(code);
          setCouponError(undefined);
        }}
      />

      {socialSheetOpen ? (
        <SocialLinkSheet
          ctaCopy="Update social link"
          onClose={() => setSocialSheetOpen(false)}
          link={socialLinkDraft}
          onChangeLink={(value) => {
            setSocialLinkDraft(value);
            setSocialLinkError(undefined);
          }}
          onSubmit={onSubmitSocialLink}
          error={socialLinkError}
          isSubmitting={isSavingSocialLink}
          copy={`${eventState.event.orgDetails?.name ?? 'The organizer'} curates every guest list. Share your Instagram, LinkedIn, or X (Twitter) profile to help us review your RSVP.`}
        />
      ) : null}
    </>
  );
}

/**
 * The confirm body: the five sections above the pay bar. Every value it prints
 * is handed to it — the only thing it works out is the slot's date/time line.
 */
function ConfirmBody({
  event,
  slot,
  lines,
  totalDisplay,
  subtotalDisplay,
  couponDiscountDisplay,
  isSubtotalFree,
  isFree,
  user,
  onBook,
  isBooking,
  requiresApproval,
  socialLink,
  onEditSocialLink,
  appliedCouponCode,
  isRedeemingCoupon,
  couponError,
  onRedeemCoupon,
  onRemoveCoupon,
  couponDraft,
  onChangeCouponDraft,
}: {
  event: Event;
  slot: EventSlot;
  lines: CartLine[];
  totalDisplay: string;
  subtotalDisplay?: string;
  couponDiscountDisplay?: string;
  isSubtotalFree: boolean;
  isFree: boolean;
  user: UserProfile;
  /** Create the booking — the bottom bar's CTA, free or paid. */
  onBook: () => void;
  isBooking: boolean;
  /** True when the cart's tier needs the organizer's approval before a seat
   * is actually confirmed — the CTA still reads "Confirm"/"Pay now", so this
   * is the only place that says the booking isn't final the moment it's sent. */
  requiresApproval: boolean;
  /** The approval-only tier's collected link, if this booking has one. */
  socialLink?: string;
  onEditSocialLink: () => void;
  appliedCouponCode?: string;
  isRedeemingCoupon: boolean;
  couponError?: string;
  onRedeemCoupon: (code: string) => void;
  onRemoveCoupon: () => void;
  couponDraft: string;
  onChangeCouponDraft: (code: string) => void;
}) {
  const totalTickets = lines.reduce((acc, line) => acc + line.quantity, 0);

  return (
    <YStack flex={1} bg="$color.background">
      <Head>
        <title>Confirm booking · Putting Scene</title>
      </Head>
      {/* Header: blank title + the shared back circle (same PSCircleButton,
          wired both ways for the iOS-26 double-ring fix — see
          authHeaderLeftOptions). Transparent like every screen's, so the root
          SafeAreaView's dropped top edge stays correct. */}
      <Stack.Screen
        options={{
          headerTransparent: true,
          headerTitle: () => <></>,
          headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButtonOnTransparentHeader />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />

      {/* PageScrollView + PSBottomBar as a flex sibling below: the frame is
          flex={1}, so flexbox ends the scroll exactly where the footer begins —
          no paddingBottom magic number to clear the bar. */}
      <PageScrollView>
        <YStack gap="$space.xl">
          <CheckoutEventHeader
            imageUrl={event.images[0]?.url ?? ''}
            title={event.title}
            slotDisplay={formatDateTime(slot.startAt)}
            venueName={event.venue.name}
            googleMapsUrl={event.venue.googleMapsUrl}
          />
          {requiresApproval && (
            <RequestNotice
              status={'pending'}
              pendingCopy={
                <PSText.Body2>{`We have limited spots for this experience. You will receive a confirmation email once ${event.orgDetails?.name ?? 'the organizer'} approves your booking.`}</PSText.Body2>
              }
            />
          )}

          {!isSubtotalFree && (
            <YStack gap="$space.md">
              <PSText.H1 color="$textDark">Have a coupon code?</PSText.H1>
              <PSCouponCodeInput
                code={couponDraft}
                appliedCode={appliedCouponCode}
                redeeming={isRedeemingCoupon}
                error={couponError}
                onRedeem={onRedeemCoupon}
                onRemove={onRemoveCoupon}
                onChangeCode={onChangeCouponDraft}
              />
            </YStack>
          )}

          {/* Cart — one line per booked tier, straight from the cart context. */}
          <CheckoutCart
            headerLabel="Cart"
            lines={lines}
            totalDisplay={totalDisplay}
            subtotalDisplay={subtotalDisplay}
            couponDiscountDisplay={couponDiscountDisplay}
          />

          {/* Your details — the booking contact. */}
          <CheckoutUserDetails
            name={user.fullName}
            // Null only for a logged-in user whose profile isn't finished —
            // a state the detail screen's CTA routes into auth before it can
            // reach this screen. See the incomplete-profile TODO in [[session]].
            email={user.email ?? ''}
            mobile={user.mobile}
            socialLink={socialLink}
            onEditSocialLink={onEditSocialLink}
          />

          {/* Terms */}
          {event.termsAndConditions && (
            <Section headerLabel="Terms &amp; conditions">
              <PSRichText markdown={event.termsAndConditions} />
            </Section>
          )}
        </YStack>
        <Footer />
      </PageScrollView>

      {/* Bottom bar. A FREE booking (or an approval-only tier, always free in
          practice, or a coupon that covers the lot) has nothing to pay, so there's no total to show and the CTA
          reads "Confirm" (not "Pay now"); with no left-hand text the lone CTA
          is pushed right (PSBottomBar lays children space-between, so a single
          child would otherwise sit left — same fix as the event page's
          external bar). A PAID booking shows the total on the left and "Pay
          now" on the right.

          Both press the SAME handler: `handleBook` is what decides whether
          that means `POST /bookings` or `POST .../participation-request` (see
          its own docstring). Hence one loading verb too — "Booking" reads
          fine for an application in flight as well, and it's honest about the
          paid path stopping short of the payment sheet for now. */}
      <PSBottomBar>
        {isFree ? (
          <XStack flex={1} justify="flex-end">
            <PSButton
              title={event.isRsvpEvent ? 'Confirm RSVP' : 'Confirm'}
              fullWidth
              loading={isBooking}
              loadingTitle={event.isRsvpEvent ? 'RSVPing' : 'Booking'}
              disabled={isRedeemingCoupon}
              onPress={onBook}
            />
          </XStack>
        ) : (
          <>
            <YStack>
              <PSText.H1>{totalDisplay}</PSText.H1>
              <PSText.Body3>
                {totalTickets} Ticket{totalTickets === 1 ? '' : 's'}
              </PSText.Body3>
            </YStack>
            <PSButton
              title="Pay now"
              loading={isBooking}
              loadingTitle="Booking"
              disabled={isRedeemingCoupon}
              onPress={onBook}
            />
          </>
        )}
      </PSBottomBar>
    </YStack>
  );
}

/**
 * A whole-screen centered message — the stand-in for the loading and errored
 * states until they're designed (they only become reachable with the
 * resume-abandoned-booking flow). Deliberately bare so it reads as a placeholder.
 */
function FullScreenMessage({ message }: { message: string }) {
  return (
    <YStack flex={1} items="center" justify="center" bg="$color.background">
      <PSText.Body2 fontSize="$large" color="$textDark">
        {message}
      </PSText.Body2>
    </YStack>
  );
}
